import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Archive,
  ArchiveRestore,
  MoreHorizontal,
  Pencil,
  Plus,
  Trash2,
} from "lucide-react";
import {
  reportTemplatesApi,
  type ReportTemplateDto,
} from "@/api/reportTemplates";
import { Badge, Button, Input } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/components/ui/Toast";
import { DataTable, type Column } from "@/components/layout/DataTable";
import { PageHeader } from "@/components/layout/PageHeader";
import { RoleChip } from "@/components/shared/RoleChip";
import { useAuth } from "@/hooks/useAuth";
import { useCategoryLabels } from "@/hooks/useCategories";
import { cn } from "@/design-system/lib/utils";

/**
 * LLM &amp; Reports v3.1 §10d — admin list page for the unified
 * ReportTemplate aggregate. Replaces v2.0's /admin/prompt-templates AND
 * /admin/form-templates with one CRUD surface. Edit page lives at
 * /admin/report-templates/{id}; new template at /admin/report-templates/new.
 */
export function ReportTemplatesPage() {
  const { hasPermission } = useAuth();
  const canManage = hasPermission("Reports.ManageTemplates");
  const qc = useQueryClient();
  const { toast } = useToast();
  const [search, setSearch] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ReportTemplateDto | null>(null);

  const list = useQuery({
    queryKey: ["report-templates-admin", search],
    queryFn: () =>
      reportTemplatesApi.adminList({
        page: 1,
        pageSize: 100,
        q: search || undefined,
      }),
  });

  // Admin-managed Lookups v1.0 §4d — resolve category codes to their
  // admin-editable display names. Falls back to the raw code while the
  // categories query is loading and for codes that no longer match an
  // active row (e.g. a template whose category was archived).
  const { titleOf: categoryLabelOf } = useCategoryLabels();

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ["report-templates-admin"] });

  const archiveMutation = useMutation({
    mutationFn: (template: ReportTemplateDto) =>
      template.isActive
        ? reportTemplatesApi.adminArchive(template.id)
        : reportTemplatesApi.adminUnarchive(template.id),
    onSuccess: (_data, variables) => {
      toast({
        tone: "success",
        message: variables.isActive
          ? `Archived '${variables.name}'. Toggle 'Show archived' to find it again.`
          : `Restored '${variables.name}'.`,
      });
      void invalidate();
    },
    onError: (e: Error) => toast({ tone: "error", message: e.message }),
  });

  const rows = (list.data?.data ?? []).filter((t) =>
    showArchived ? true : t.isActive,
  );
  const archivedCount = (list.data?.data ?? []).filter((t) => !t.isActive).length;

  const columns: Column<ReportTemplateDto>[] = [
    {
      key: "name",
      header: "Template",
      render: (t) => (
        <Link
          to={`/admin/report-templates/${t.id}`}
          className={cn(
            "font-semibold text-onyx hover:text-gold-shadow",
            !t.isActive && "opacity-50",
          )}
        >
          <div className="flex items-center gap-space-2">
            <span>{t.name}</span>
            {!t.isActive && <Badge status="archived">Archived</Badge>}
          </div>
          <div className="text-caption text-gray-mid font-normal max-w-md truncate">
            {t.description}
          </div>
        </Link>
      ),
    },
    {
      key: "category",
      header: "Category",
      render: (t) => (
        <span
          className={cn(!t.isActive && "opacity-50")}
          title={t.category}
        >
          <Badge status="info">{categoryLabelOf(t.category) ?? t.category}</Badge>
        </span>
      ),
    },
    {
      key: "roles",
      header: "Roles",
      render: (t) => (
        <div className={cn("flex flex-wrap gap-space-1", !t.isActive && "opacity-50")}>
          {t.roleCodes.map((r) => (
            <RoleChip key={r} code={r} />
          ))}
        </div>
      ),
    },
    {
      key: "format",
      header: "Output",
      render: (t) => (
        <span className={cn("font-mono text-caption", !t.isActive && "opacity-50")}>
          {t.outputFormat}
        </span>
      ),
    },
    {
      key: "master",
      header: "Master doc",
      render: (t) => (
        <span className={cn(!t.isActive && "opacity-50")}>
          {t.masterDocumentId ? (
            <Badge status="finalized">Yes</Badge>
          ) : (
            <span className="text-caption text-gray-mid">—</span>
          )}
        </span>
      ),
    },
    {
      key: "shape",
      header: "Form",
      render: (t) => (
        <div className={cn("text-caption", !t.isActive && "opacity-50")}>
          <span className="font-mono">{t.sections.length}</span> sections ·{" "}
          <span className="font-mono">
            {t.sections.reduce((acc, s) => acc + s.questions.length, 0)}
          </span>{" "}
          questions
        </div>
      ),
    },
    {
      key: "version",
      header: "Version",
      render: (t) => (
        <div className={cn("flex items-center gap-space-2", !t.isActive && "opacity-50")}>
          <span className="font-mono text-caption">v{t.version}</span>
        </div>
      ),
    },
    {
      key: "actions",
      header: "",
      render: (t) =>
        canManage ? (
          <RowActions
            template={t}
            archivePending={archiveMutation.isPending}
            onArchiveToggle={() => archiveMutation.mutate(t)}
            onDelete={() => setDeleteTarget(t)}
          />
        ) : null,
    },
  ];

  return (
    <>
      <PageHeader
        eyebrow="Admin"
        title="Report templates"
        description="Manage report templates — name, prompt body, role assignments, optional master document, and the embedded form (sections + questions)."
        actions={
          canManage && (
            <Link to="/admin/report-templates/new">
              <Button>
                <Plus size={16} aria-hidden /> New template
              </Button>
            </Link>
          )
        }
      />

      <div className="flex items-center gap-space-3 mb-space-3 flex-wrap">
        <Input
          type="search"
          placeholder="Search templates…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-sm"
        />
        <button
          type="button"
          onClick={() => setShowArchived((v) => !v)}
          aria-pressed={showArchived}
          className={cn(
            "px-space-2 py-[6px] rounded-full text-caption font-display font-semibold border transition-colors",
            showArchived
              ? "bg-gold/15 border-gold text-gold-shadow"
              : "bg-white-pure border-border-light text-gray-mid hover:border-gold-shadow",
          )}
        >
          Show archived{archivedCount > 0 ? ` (${archivedCount})` : ""}
        </button>
      </div>

      <DataTable
        columns={columns}
        rows={rows}
        loading={list.isLoading}
        error={list.isError ? (list.error as Error).message : null}
        emptyMessage={
          canManage ? "No report templates yet. Click 'New template' to build one." : "No templates available."
        }
        rowKey={(t) => t.id}
      />

      {deleteTarget && (
        <DeleteModal
          template={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirmed={() => {
            setDeleteTarget(null);
            void invalidate();
          }}
        />
      )}
    </>
  );
}

function RowActions({
  template,
  archivePending,
  onArchiveToggle,
  onDelete,
}: {
  template: ReportTemplateDto;
  archivePending: boolean;
  onArchiveToggle: () => void;
  onDelete: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const canDelete = template.generatedReportCount === 0;
  const deleteTooltip = canDelete
    ? undefined
    : `Cannot delete — this template has been used to generate ${template.generatedReportCount} report${template.generatedReportCount === 1 ? "" : "s"}. Archive it instead.`;

  return (
    <div className="flex items-center gap-space-1 justify-end" ref={ref}>
      <Link
        to={`/admin/report-templates/${template.id}`}
        className="p-space-1 rounded-md text-gold-shadow hover:bg-white-warm"
        aria-label={`Edit ${template.name}`}
      >
        <Pencil size={16} aria-hidden />
      </Link>
      <div className="relative">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="p-space-1 rounded-md text-gray-mid hover:bg-white-warm hover:text-onyx"
          aria-label={`More actions for ${template.name}`}
          aria-haspopup="menu"
          aria-expanded={open}
        >
          <MoreHorizontal size={16} aria-hidden />
        </button>
        {open && (
          <div
            role="menu"
            className="absolute right-0 mt-1 w-56 z-20 bg-white-pure border border-border-light rounded-md shadow-lg overflow-hidden"
          >
            <button
              type="button"
              role="menuitem"
              disabled={archivePending}
              onClick={() => {
                setOpen(false);
                onArchiveToggle();
              }}
              className="w-full flex items-center gap-space-2 px-space-3 py-space-2 text-small text-onyx hover:bg-white-warm disabled:opacity-50"
            >
              {template.isActive ? (
                <>
                  <Archive size={14} aria-hidden /> Archive
                </>
              ) : (
                <>
                  <ArchiveRestore size={14} aria-hidden /> Unarchive
                </>
              )}
            </button>
            <button
              type="button"
              role="menuitem"
              disabled={!canDelete}
              title={deleteTooltip}
              onClick={() => {
                setOpen(false);
                onDelete();
              }}
              className="w-full flex items-center gap-space-2 px-space-3 py-space-2 text-small text-status-error hover:bg-white-warm disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
            >
              <Trash2 size={14} aria-hidden /> Delete
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function DeleteModal({
  template,
  onClose,
  onConfirmed,
}: {
  template: ReportTemplateDto;
  onClose: () => void;
  onConfirmed: () => void;
}) {
  const { toast } = useToast();
  const mutation = useMutation({
    mutationFn: () => reportTemplatesApi.adminDelete(template.id),
    onSuccess: () => {
      toast({ tone: "success", message: `Deleted '${template.name}'.` });
      onConfirmed();
    },
    onError: (e: Error) => toast({ tone: "error", message: e.message }),
  });
  return (
    <Modal
      open
      onClose={onClose}
      title={`Delete '${template.name}'?`}
      description="This permanently removes the template. This cannot be undone."
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={() => mutation.mutate()}
            loading={mutation.isPending}
          >
            Delete permanently
          </Button>
        </>
      }
    >
      <p className="text-small text-gray-mid">
        Anything that previously linked to this template will lose its
        reference. If you need to keep the template's history, archive it
        instead.
      </p>
      <div className="text-caption text-gray-mid mt-space-2">
        v{template.version} · {template.category}
      </div>
    </Modal>
  );
}
